import React, { useEffect, useState } from 'react';
import { useDownloads } from '../context/DownloadManagerContext';
import { getManifest, formatBytes, estimateStorage } from '../lib/offline/offlineStore';
import { useTranslation } from '../contexts/LanguageContext';
import ConfirmDialog from './ConfirmDialog';
import '../styles/components/OfflineSettingsSection.css';

// Statuses the Active block surfaces — everything still in flight or that
// needs the user's attention. 'done' items are handled by the Downloaded
// list below via downloadedIds instead.
const ACTIVE_STATUSES = ['queued', 'downloading', 'error'];

export default function OfflineSettingsSection() {
  // `items` defaults to [] — some call sites (Header.test.js, SettingsSheet.test.js)
  // stub useDownloads() with only { downloadedIds, removeEpisode } since they
  // only exercise the Downloaded list, not the Active block.
  const { downloadedIds, removeEpisode, items = [], cancel, retry } = useDownloads();
  const { t } = useTranslation();
  const manifest = getManifest();
  const [storage, setStorage] = useState({ usage: 0, quota: 0 });
  const [confirmOpen, setConfirmOpen] = useState(false);
  const activeItems = items.filter((item) => ACTIVE_STATUSES.includes(item.status));

  // Storage-quota reporting is orthogonal to the download queue itself, so it
  // is tracked locally here rather than through DownloadManagerContext.
  // Re-checked whenever the downloaded set changes (download/remove/clear).
  useEffect(() => {
    let cancelled = false;
    estimateStorage().then((est) => { if (!cancelled) setStorage(est); });
    return () => { cancelled = true; };
  }, [downloadedIds]);

  // No bulk "remove all" on the context (the download manager owns the queue,
  // not general offline storage) — remove each downloaded episode through the
  // context so `downloadedIds` (its single source of truth) always stays in
  // sync for every other consumer (EpisodeRow, ExpandedPlayer, ...).
  const clearAll = () => { downloadedIds.forEach((id) => removeEpisode(id)); };

  return (
    <div className="offline-settings">
      {activeItems.length > 0 && (
        <div className="offline-settings__active">
          <h4 className="offline-settings__active-title">{t('downloads.active')}</h4>
          <ul className="offline-settings__active-list">
            {activeItems.map((item) => (
              <li key={item.id} className="offline-settings__active-item">
                <span className="offline-settings__active-episode-title">{item.episode.title}</span>
                <span className="offline-settings__active-status">
                  {item.status === 'downloading'
                    ? `${Math.round(item.progress || 0)}%`
                    : item.status === 'error'
                      ? t('offline.downloadFailed')
                      : t('offline.queued')}
                </span>
                {item.status === 'error' ? (
                  <button
                    type="button"
                    className="offline-settings__retry-btn"
                    onClick={() => retry(item.id)}
                    aria-label={`${t('downloads.retry')} ${item.episode.title}`}
                  >
                    {t('downloads.retry')}
                  </button>
                ) : (
                  <button
                    type="button"
                    className="offline-settings__cancel-btn"
                    onClick={() => cancel(item.id)}
                    aria-label={`${t('downloads.cancel')} ${item.episode.title}`}
                  >
                    {t('downloads.cancel')}
                  </button>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="offline-settings__storage">
        <span className="offline-settings__storage-label">{t('offline.storageUsed')}: </span>
        <span className="offline-settings__storage-value">
          {formatBytes(storage.usage)} / {formatBytes(storage.quota)}
        </span>
      </div>

      {downloadedIds.length === 0 ? (
        <p className="offline-settings__empty">{t('offline.noDownloads')}</p>
      ) : (
        <ul className="offline-settings__list">
          {downloadedIds.map((id) => {
            const entry = manifest[id];
            if (!entry) return null;
            return (
              <li key={id} className="offline-settings__episode">
                <span className="offline-settings__episode-title">{entry.title}</span>
                <span className="offline-settings__episode-size">{formatBytes(entry.bytes)}</span>
                <button
                  className="offline-settings__remove-btn"
                  onClick={() => removeEpisode(id)}
                  aria-label={`${t('offline.removeOffline')} ${entry.title}`}
                >
                  &#x2715;
                </button>
              </li>
            );
          })}
        </ul>
      )}

      <button
        className="offline-settings__clear-btn"
        onClick={() => setConfirmOpen(true)}
      >
        {t('offline.removeAll')}
      </button>

      <ConfirmDialog
        open={confirmOpen}
        title={t('confirm.removeAllDownloads.title')}
        message={t('confirm.removeAllDownloads.message')}
        confirmLabel={t('confirm.confirm')}
        cancelLabel={t('confirm.cancel')}
        onConfirm={() => { clearAll(); setConfirmOpen(false); }}
        onCancel={() => setConfirmOpen(false)}
      />
    </div>
  );
}
